import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from "@angular/core";
import {FormsModule} from "@angular/forms";
import {Platform} from "@angular/cdk/platform";
import {TranslateModule} from "@ngx-translate/core";
import {DiscussionDraft} from "../../interfaces/discussion.interface";
import {ScreenSizeService} from "src/app/modules/shared/services/screenSize.service";
import {PlatformService} from "src/app/services/platform.service";
import {getElementHorizontalOverflowMask} from "src/app/utils/overflow-mask.util";

@Component({
  selector: "app-discussion-composer",
  templateUrl: "./discussion-composer.component.html",
  imports: [FormsModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscussionComposerComponent implements OnChanges, OnDestroy {
  private readonly screenSize = inject(ScreenSizeService);
  private readonly platform = inject(Platform);
  @Input() suggestions: string[] = [];
  @Input() recipient = "";
  @Input() sending = false;
  @Input() sentRevision = 0;
  @Input() disabled = false;
  @Input() readOnlyReason = "DISCUSSION.READ_ONLY";
  @Output() sendMessage = new EventEmitter<DiscussionDraft>();
  @Output() typing = new EventEmitter<boolean>();
  @ViewChild("input") input?: ElementRef<HTMLTextAreaElement>;
  text = "";
  spoiler = false;
  private previousRevision = 0;
  private pendingDraft?: DiscussionDraft;
  private suggestionsElement?: HTMLElement;
  private suggestionsObserver?: ResizeObserver;
  @ViewChild("suggestionsRail") set suggestionsRail(
    ref: ElementRef<HTMLElement> | undefined
  ) {
    this.suggestionsObserver?.disconnect();
    this.suggestionsElement = ref?.nativeElement;
    if (!this.suggestionsElement) return;
    this.suggestionsObserver = new ResizeObserver(() =>
      this.updateSuggestionsMask()
    );
    this.suggestionsObserver.observe(this.suggestionsElement);
    const content = this.suggestionsElement.firstElementChild;
    if (content) this.suggestionsObserver.observe(content);
  }

  updateSuggestionsMask(): void {
    if (!this.suggestionsElement) return;
    this.suggestionsElement.style.maskImage = getElementHorizontalOverflowMask(
      this.suggestionsElement
    );
  }

  ngOnDestroy(): void {
    this.typing.emit(false);
    this.suggestionsObserver?.disconnect();
    this.suggestionsElement = undefined;
  }
  ngOnChanges(): void {
    if (this.disabled || this.sending) this.typing.emit(false);
    if (this.sentRevision !== this.previousRevision) {
      this.text = "";
      this.spoiler = false;
      this.pendingDraft = undefined;
      this.previousRevision = this.sentRevision;
    }
  }

  suggest(text: string) {
    this.text = text;
    this.input?.nativeElement.focus();
    this.typing.emit(!!text.trim());
  }

  send() {
    if (
      this.sending ||
      this.disabled ||
      !this.text.trim() ||
      this.text.length > 2000
    )
      return;
    const text = this.text.trim();
    if (
      this.pendingDraft?.text !== text ||
      this.pendingDraft.spoiler !== this.spoiler
    ) {
      this.pendingDraft = {
        text,
        spoiler: this.spoiler,
        clientId: crypto.randomUUID(),
      };
    }
    this.typing.emit(false);
    this.sendMessage.emit(this.pendingDraft);
  }

  keydown(event: KeyboardEvent) {
    if (
      event.key !== "Enter" ||
      event.shiftKey ||
      event.isComposing ||
      this.mobileInput
    )
      return;
    event.preventDefault();
    if (!event.repeat) this.send();
  }

  get mobileInput(): boolean {
    return (
      PlatformService.isNativePlatform() ||
      this.screenSize.currentScreenSize.isFullLayoutMobile ||
      this.platform.ANDROID ||
      this.platform.IOS
    );
  }
}
